import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { CRITICIDADES, type CriticidadValor } from '../../../domain/value-objects/Criticidad';
import { Criticidad } from '../../../domain/value-objects/Criticidad';
import type { Sensor } from '../../../domain/entities/Sensor';
import type {
  DatosUmbralAnalogico,
  DatosUmbralDigital,
  ReglaActuador,
  TipoUmbral,
  UmbralConfigurado,
  VentanaHoraria,
} from '../../../data/repositories/RepositorioUmbrales';
import type { Actuador } from '../../../domain/entities/Actuador';
import { EditorHorarios } from './EditorHorarios';
import { EditorReglasActuador } from './EditorReglasActuador';
import estilos from './Umbrales.module.css';

/** Todo lo que hay que guardar de un umbral, incluidos sus anexos. */
export interface UmbralCompleto {
  datos: DatosUmbralAnalogico | DatosUmbralDigital;
  horarios: VentanaHoraria[];
  reglas: ReglaActuador[];
}

interface FormularioUmbralProps {
  abierto: boolean;
  tipo: TipoUmbral;
  /** `null` para crear uno nuevo. */
  umbral: UmbralConfigurado | null;
  sensores: readonly Sensor[];
  actuadores: readonly Actuador[];
  guardando: boolean;
  onCerrar: () => void;
  onGuardar: (completo: UmbralCompleto) => void;
}

/** Estado del formulario, todo como texto para que los campos vacíos existan. */
interface Campos {
  sensorId: string;
  valorMin: string;
  valorMax: string;
  histeresis: string;
  retardo: string;
  inhibicion: string;
  estadoEsperado: string;
  criticidad: CriticidadValor;
  mensaje: string;
  activo: boolean;
  retencion: boolean;
  notificar: boolean;
}

const CAMPOS_INICIALES: Campos = {
  sensorId: '',
  valorMin: '',
  valorMax: '',
  histeresis: '',
  retardo: '0',
  inhibicion: '0',
  estadoEsperado: 'true',
  criticidad: 'media',
  mensaje: '',
  activo: true,
  retencion: true,
  notificar: true,
};

function aNumeroOpcional(texto: string): number | null {
  const t = texto.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Formulario de alta y edición de un umbral, analógico o digital. */
export function FormularioUmbral({
  abierto,
  tipo,
  umbral,
  sensores,
  actuadores,
  guardando,
  onCerrar,
  onGuardar,
}: FormularioUmbralProps) {
  const [campos, setCampos] = useState<Campos>(CAMPOS_INICIALES);
  const [horarios, setHorarios] = useState<VentanaHoraria[]>([]);
  const [reglas, setReglas] = useState<ReglaActuador[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Acotado de la lista de sensores. Se guarda por nombre porque es lo que
  // trae el sensor ya resuelto y evita otra consulta sólo para el desplegable.
  const [cliente, setCliente] = useState('');
  const [area, setArea] = useState('');
  const [nodo, setNodo] = useState('');

  const esAnalogico = tipo === 'analogico';

  // Al abrir se carga el umbral que se va a editar, o se limpia para uno nuevo.
  useEffect(() => {
    if (!abierto) return;

    setError(null);
    setHorarios(umbral ? umbral.horarios.map((h) => ({ ...h })) : []);
    setReglas(umbral ? umbral.reglas.map((r) => ({ ...r })) : []);

    // Al editar se parte de la ubicación del umbral, para que su sensor esté
    // visible en el desplegable desde el primer momento.
    setCliente(umbral?.clienteNombre ?? '');
    setArea(umbral?.areaNombre ?? '');
    setNodo(umbral?.nodoNombre ?? '');
    setCampos(
      umbral
        ? {
            sensorId: String(umbral.sensorId ?? ''),
            valorMin: umbral.valorMinimo?.toString() ?? '',
            valorMax: umbral.valorMaximo?.toString() ?? '',
            histeresis: umbral.histeresis?.toString() ?? '',
            retardo: String(umbral.retardoSegundos ?? 0),
            inhibicion: String(umbral.inhibicionArranqueSegundos ?? 0),
            estadoEsperado: String(umbral.estadoEsperado ?? true),
            criticidad: umbral.criticidad.valor,
            mensaje: umbral.mensaje ?? '',
            activo: umbral.activo,
            retencion: umbral.retencion,
            notificar: umbral.notificar,
          }
        : CAMPOS_INICIALES,
    );
  }, [abierto, umbral]);

  /**
   * Sensores elegibles: los del tipo que corresponde al umbral, acotados por
   * cliente, área y nodo.
   *
   * Sin este acotado la lista es un desplegable con todos los sensores de
   * todas las instalaciones, imposible de recorrer en cuanto hay más de unas
   * pocas decenas.
   */
  const sensoresDisponibles = useMemo(
    () =>
      sensores.filter((s) => {
        if (s.esDigital !== !esAnalogico) return false;
        if (cliente && s.clienteNombre !== cliente) return false;
        if (area && s.areaNombre !== area) return false;
        if (nodo && s.nodoNombre !== nodo) return false;
        return true;
      }),
    [sensores, esAnalogico, cliente, area, nodo],
  );

  /** Opciones de cada nivel, derivadas de lo que hay por encima. */
  const clientes = useMemo(
    () => [...new Set(sensores.map((s) => s.clienteNombre).filter((n): n is string => !!n))].sort(),
    [sensores],
  );

  const areas = useMemo(
    () =>
      [
        ...new Set(
          sensores
            .filter((s) => !cliente || s.clienteNombre === cliente)
            .map((s) => s.areaNombre)
            .filter((n): n is string => !!n),
        ),
      ].sort(),
    [sensores, cliente],
  );

  const nodos = useMemo(
    () =>
      [
        ...new Set(
          sensores
            .filter(
              (s) => (!cliente || s.clienteNombre === cliente) && (!area || s.areaNombre === area),
            )
            .map((s) => s.nodoNombre)
            .filter((n): n is string => !!n),
        ),
      ].sort(),
    [sensores, cliente, area],
  );

  const enviar = () => {
    setError(null);

    const sensorId = Number(campos.sensorId);
    if (!Number.isFinite(sensorId) || sensorId <= 0) {
      setError('Elige el sensor al que se aplica este umbral.');
      return;
    }

    const base = {
      sensor_id: sensorId,
      debounce_segundos: aNumeroOpcional(campos.retardo) ?? 0,
      criticidad: campos.criticidad,
      mensaje: campos.mensaje.trim() || null,
      activo: campos.activo,
      latched: campos.retencion,
      notificar: campos.notificar,
    };

    // Una franja sin días no se aplicaría nunca; es un error de configuración
    // silencioso que conviene atajar aquí.
    if (horarios.some((h) => h.dias.length === 0)) {
      setError('Hay una franja horaria sin días marcados. Marca al menos uno o quítala.');
      return;
    }
    if (horarios.some((h) => h.horaInicio === h.horaFin)) {
      setError('Una franja horaria no puede empezar y terminar a la misma hora.');
      return;
    }

    if (!esAnalogico) {
      onGuardar({
        datos: { ...base, estado_esperado: campos.estadoEsperado === 'true' },
        horarios,
        reglas,
      });
      return;
    }

    const minimo = aNumeroOpcional(campos.valorMin);
    const maximo = aNumeroOpcional(campos.valorMax);

    if (minimo === null && maximo === null) {
      setError('Indica al menos un límite: mínimo, máximo o los dos.');
      return;
    }
    if (minimo !== null && maximo !== null && minimo >= maximo) {
      setError('El mínimo debe ser menor que el máximo.');
      return;
    }

    onGuardar({
      datos: {
        ...base,
        valor_min: minimo,
        valor_max: maximo,
        deadband: aNumeroOpcional(campos.histeresis),
        inhibicion_arranque_segundos: aNumeroOpcional(campos.inhibicion) ?? 0,
      },
      horarios,
      reglas,
    });
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={umbral ? 'Editar umbral' : 'Nuevo umbral'}
      descripcion={
        esAnalogico
          ? 'Se avisa cuando el valor del sensor sale del rango indicado.'
          : 'Se avisa cuando el sensor deja de estar en el estado esperado.'
      }
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={enviar} cargando={guardando} icono="guardar">
            Guardar
          </Button>
        </>
      }
    >
      <div className={estilos.formulario}>
        {error && <div className={estilos.errorFormulario}>{error}</div>}

        <div className={estilos.filaCampos}>
          <Select
            etiqueta="Cliente"
            placeholder="Todos"
            value={cliente}
            onChange={(e) => {
              setCliente(e.target.value);
              setArea('');
              setNodo('');
            }}
            opciones={clientes.map((c) => ({ valor: c, etiqueta: c }))}
          />
          <Select
            etiqueta="Área"
            placeholder="Todas"
            value={area}
            onChange={(e) => {
              setArea(e.target.value);
              setNodo('');
            }}
            opciones={areas.map((a) => ({ valor: a, etiqueta: a }))}
          />
          <Select
            etiqueta="Nodo"
            placeholder="Todos"
            value={nodo}
            onChange={(e) => setNodo(e.target.value)}
            opciones={nodos.map((n) => ({ valor: n, etiqueta: n }))}
          />

          {/* El sensor cierra la cascada, así que va en la misma rejilla. */}
          <Select
            etiqueta="Sensor"
            requerido
            placeholder={
              sensoresDisponibles.length === 0
                ? 'Ningún sensor con estos filtros'
                : 'Elige un sensor'
            }
            value={campos.sensorId}
            onChange={(e) => setCampos((c) => ({ ...c, sensorId: e.target.value }))}
            ayuda={
              umbral
                ? 'Cambiarlo mueve el umbral a otro punto de medición'
                : `${sensoresDisponibles.length} disponibles`
            }
            opciones={sensoresDisponibles.map((s) => ({
              valor: s.id,
              etiqueta: s.nombre,
            }))}
          />
        </div>

        {esAnalogico ? (
          <>
            <div className={estilos.filaCampos}>
              <Input
                etiqueta="Valor mínimo"
                type="number"
                step="any"
                placeholder="Sin mínimo"
                value={campos.valorMin}
                onChange={(e) => setCampos((c) => ({ ...c, valorMin: e.target.value }))}
                ayuda="Por debajo de este valor se avisa"
              />
              <Input
                etiqueta="Valor máximo"
                type="number"
                step="any"
                placeholder="Sin máximo"
                value={campos.valorMax}
                onChange={(e) => setCampos((c) => ({ ...c, valorMax: e.target.value }))}
                ayuda="Por encima de este valor se avisa"
              />
            </div>

            <div className={estilos.filaCampos}>
              <Input
                etiqueta="Margen de recuperación"
                type="number"
                step="any"
                min={0}
                placeholder="Sin margen"
                value={campos.histeresis}
                onChange={(e) => setCampos((c) => ({ ...c, histeresis: e.target.value }))}
                ayuda="Evita que la alarma parpadee al rozar el límite"
              />
              <Input
                etiqueta="Espera tras el arranque (s)"
                type="number"
                min={0}
                value={campos.inhibicion}
                onChange={(e) => setCampos((c) => ({ ...c, inhibicion: e.target.value }))}
                ayuda="Se ignora el sensor durante este tiempo al encender el equipo"
              />
            </div>
          </>
        ) : (
          <Select
            etiqueta="Estado esperado"
            value={campos.estadoEsperado}
            onChange={(e) => setCampos((c) => ({ ...c, estadoEsperado: e.target.value }))}
            ayuda="Se avisa cuando el sensor deja de estar en este estado"
            opciones={[
              { valor: 'true', etiqueta: 'Encendido (ON)' },
              { valor: 'false', etiqueta: 'Apagado (OFF)' },
            ]}
          />
        )}

        <div className={estilos.filaCampos}>
          <Select
            etiqueta="Gravedad"
            value={campos.criticidad}
            onChange={(e) =>
              setCampos((c) => ({ ...c, criticidad: e.target.value as CriticidadValor }))
            }
            opciones={CRITICIDADES.map((c) => ({
              valor: c,
              etiqueta: Criticidad.from(c).etiqueta,
            }))}
          />
          <Input
            etiqueta="Retardo antes de avisar (s)"
            type="number"
            min={0}
            value={campos.retardo}
            onChange={(e) => setCampos((c) => ({ ...c, retardo: e.target.value }))}
            ayuda="La condición debe mantenerse este tiempo"
          />
        </div>

        <Input
          etiqueta="Mensaje de la alarma"
          placeholder="Opcional: qué debe hacer quien reciba el aviso"
          value={campos.mensaje}
          onChange={(e) => setCampos((c) => ({ ...c, mensaje: e.target.value }))}
        />

        <div className={estilos.opciones}>
          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.activo}
              onChange={(e) => setCampos((c) => ({ ...c, activo: e.target.checked }))}
            />
            <span>
              <strong>Umbral activo</strong>
              <span className={estilos.opcionAyuda}>Si se desactiva, deja de vigilarse</span>
            </span>
          </label>

          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.retencion}
              onChange={(e) => setCampos((c) => ({ ...c, retencion: e.target.checked }))}
            />
            <span>
              <strong>Exigir cierre manual</strong>
              <span className={estilos.opcionAyuda}>
                La alarma sigue abierta aunque el valor vuelva a normal, hasta que alguien la
                resuelva. Sin esto no se podrá reconocer ni resolver desde la pantalla de alarmas.
              </span>
            </span>
          </label>

          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.notificar}
              onChange={(e) => setCampos((c) => ({ ...c, notificar: e.target.checked }))}
            />
            <span>
              <strong>Enviar notificaciones</strong>
              <span className={estilos.opcionAyuda}>
                Avisa a los contactos de alerta configurados
              </span>
            </span>
          </label>
        </div>

        {/* El horario sólo existe para umbrales digitales. */}
        {!esAnalogico && <EditorHorarios horarios={horarios} onCambiar={setHorarios} />}

        <EditorReglasActuador reglas={reglas} actuadores={actuadores} onCambiar={setReglas} />
      </div>
    </Modal>
  );
}
